import { useCallback, useEffect, useRef, useState } from 'react';
import { Svg } from '../art/icons.js';
import { qrSvg } from '../lib/qr.js';
import { fmt, num } from '../lib/format.js';
import { api } from '../lib/api.js';
import { featureOn, useStore } from '../lib/store.js';
import type { CardDto } from '../lib/types.js';
import { logoUrlFor, useLook } from '../lib/look.js';
import { designStyle } from '../lib/appearance.js';
import { designAttrs, designLayers, type CardDesign } from '@tikkas/shared';
import { countUpValue } from '../lib/intro.js';
import { useRotatingToken } from './CodeSheet.js';

/*
 * The Tikkas card on Home (CLAUDE.md §4 "Card", Saif 21 Oct 2026 evening): a real debit card in the brand colour with the
 * balance as the focus, two large numbers side by side (credit and points), the first name small, the level or Founders
 * badge in the corner, the logo top-left, and a three-dot button bottom-right that opens the card's menu (Activity, Wallet,
 * how points work). The design's layers and movement (CARD_CSS), a slight tilt with the phone (theme.motion), and the numbers count up.
 */

const DOTS = '<svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true"><circle cx="5" cy="12" r="2.2"/><circle cx="12" cy="12" r="2.2"/><circle cx="19" cy="12" r="2.2"/></svg>';

/** The status on the card: a badge (Founders Club, given by staff) first, else the automatic level when levels are on. */
export function cardBadge(s: ReturnType<typeof useStore>, card: CardDto | null): string | null {
  const u = s.me?.customer;
  if (!u) return null;
  if (card?.badges[0]) return card.badges[0].label;
  if (u.group === 'founder') return 'Founders Club';
  if (featureOn(s, 'levels') && (card?.level?.name ?? u.level?.name)) return (card?.level?.name ?? u.level?.name)!;
  return null;
}

/** GET /api/me/card (docs/23 §3.1), refreshed when points or credit change; null until it answers. */
export function useCard(s: ReturnType<typeof useStore>): CardDto | null {
  const [card, setCard] = useState<CardDto | null>(null);
  const u = s.me?.customer;
  useEffect(() => {
    if (!u) return setCard(null);
    api<CardDto>('/api/me/card').then(setCard).catch(() => setCard(null));
  }, [u?.id, u?.points, u?.creditPence, u?.group]); // eslint-disable-line react-hooks/exhaustive-deps
  return card;
}

/**
 * The card's slight tilt with the phone (theme.motion): a few degrees from the device orientation. iOS asks the user
 * before giving orientation events, and only from a tap, so the permission is asked for on the first tap of the card and
 * nothing waits on it. Off under Reduce Motion.
 */
function useTilt(enabled: boolean): { style: React.CSSProperties; ask: () => void } {
  // (-1..1 of the tilt also rides as --tx / --ty, which the holographic and parallax layers follow.)
  const [rot, setRot] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const needsAsk = typeof window !== 'undefined' && 'DeviceOrientationEvent' in window && typeof (DeviceOrientationEvent as unknown as { requestPermission?: unknown }).requestPermission === 'function';
  const [granted, setGranted] = useState<boolean>(() => typeof window !== 'undefined' && 'DeviceOrientationEvent' in window && !needsAsk);
  const reduce = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const on = enabled && granted && !reduce;
  useEffect(() => {
    if (!on) return;
    let frame = 0;
    let next = { x: 0, y: 0 };
    const onMove = (e: DeviceOrientationEvent) => {
      const beta = e.beta ?? 45, gamma = e.gamma ?? 0;
      next = { x: Math.max(-4, Math.min(4, -(beta - 45) / 12)), y: Math.max(-4, Math.min(4, gamma / 10)) };
      if (!frame) frame = requestAnimationFrame(() => { frame = 0; setRot(next); });
    };
    window.addEventListener('deviceorientation', onMove);
    return () => {
      window.removeEventListener('deviceorientation', onMove);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [on]);
  const ask = useCallback(() => {
    if (!enabled || granted || reduce || !needsAsk) return;
    const req = (DeviceOrientationEvent as unknown as { requestPermission: () => Promise<string> }).requestPermission;
    req().then((r) => r === 'granted' && setGranted(true)).catch(() => undefined);
  }, [enabled, granted, reduce, needsAsk]);
  return { style: on ? ({ transform: `perspective(900px) rotateX(${rot.x.toFixed(2)}deg) rotateY(${rot.y.toFixed(2)}deg)`, '--tx': (rot.x / 4).toFixed(3), '--ty': (rot.y / 4).toFixed(3) } as React.CSSProperties) : {}, ask };
}

/**
 * What every card shares (Home's card, the join card, the join page's benefits card): the design's variables and
 * attributes on the section, the layer elements first inside it, and the logo as a mask in the design's logo colour.
 */
export function cardFace(d: CardDesign, motion: boolean, logoUrl: string, still = false): { className: string; style: React.CSSProperties; attrs: Record<string, string> } {
  return { className: `bankcard${motion && !still ? ' animate' : ''}`, style: designStyle(d, logoUrl) as React.CSSProperties, attrs: designAttrs(d) };
}
export const CardLayers = ({ d }: { d: CardDesign }) => <>{designLayers(d).map((cls) => <i key={cls} className={cls} aria-hidden="true" />)}</>;
export const CardLogo = (props: { 'data-intro-target'?: string }) => <span className="bc-logowrap" role="img" aria-label="Tikkas" {...props}><i className="bc-logomask hi" /><i className="bc-logomask" /></span>;

/** Points and credit roll up to their values on first paint (theme.motion; off under Reduce Motion): a count-up over 800 ms, once. */
function useCountUp(target: number, enabled: boolean): number {
  const reduce = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const on = enabled && !reduce;
  const [shown, setShown] = useState(on ? 0 : target);
  const landed = useRef(!on);
  // Behind the glide intro the numbers wait; they count up once the mark has landed (Saif, 22 Oct).
  const [ready, setReady] = useState(() => typeof document === 'undefined' || !document.documentElement.hasAttribute('data-intro'));
  useEffect(() => {
    if (ready) return;
    const go = () => setReady(true);
    window.addEventListener('tikkas:intro-done', go);
    const t = setTimeout(go, 2500); // never wait on an intro that doesn't come
    return () => { window.removeEventListener('tikkas:intro-done', go); clearTimeout(t); };
  }, [ready]);
  useEffect(() => {
    if (landed.current) return setShown(target);
    if (!ready) return;
    const start = performance.now();
    let frame = 0;
    const tick = (now: number) => {
      const t = (now - start) / 800;
      setShown(countUpValue(target, t));
      if (t < 1) frame = requestAnimationFrame(tick);
      else landed.current = true;
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, ready]); // eslint-disable-line react-hooks/exhaustive-deps
  return shown;
}

export function TikkasCard({ card, onMenu, onCode }: { card: CardDto | null; onMenu: () => void; onCode: () => void }) {
  const s = useStore();
  const { v } = useLook();
  const u = s.me?.customer;
  const badge = cardBadge(s, card);
  const tilt = useTilt(v.theme.motion);
  const points = useCountUp(u?.points ?? 0, v.theme.motion), credit = useCountUp(u?.creditPence ?? 0, v.theme.motion);
  const front = v.card.qrStyle === 'front';
  const { token, sec } = useRotatingToken(front ? s.offline : null);
  const name = s.offline?.name || u?.firstName || 'Rewards Club member';
  // The card's design (CLAUDE.md §4 "Card designs"): worked out on the server from the customer's tags and the rules; the
  // default until /api/me answers. Its colours ride as variables on the card (styles.css .bankcard).
  const design = s.me?.card?.design ?? card?.design ?? v.cardDesign;
  const face = cardFace(design, v.theme.motion, logoUrlFor(v));
  return (
    <section className={`${face.className} focus`} aria-label={`Your Tikkas card, ${design.name}`} {...face.attrs} style={{ ...face.style, ...tilt.style }} onClick={tilt.ask}>
      <CardLayers d={design} />
      <div className="bc-top">
        <CardLogo data-intro-target="" />
        {badge ? <span className="tcard-badge">{badge}</span> : null}
      </div>
      <div className="bc-bal2">
        <span className="bc-big"><b className="tnum">{fmt(credit)}</b><span>credit</span></span>
        <i className="bc-div" aria-hidden="true" />
        <span className="bc-big"><b className="tnum">{num(points)}</b><span>points</span></span>
      </div>
      <div className="bc-foot">
        <span className="bc-who"><span className="bc-name one">{name}</span><span className="bc-kick">Rewards Club</span></span>
        {front ? <button type="button" className="bc-qr small" aria-label={`Your rotating code for the till. Tap to enlarge. New code in ${sec} seconds`} onClick={(e) => { e.stopPropagation(); onCode(); }}>{token ? <Svg html={qrSvg(token)} /> : null}</button> : null}
        <button type="button" className="bc-menu" aria-label="Card menu: activity, wallet and how points work" onClick={(e) => { e.stopPropagation(); onMenu(); }}><Svg html={DOTS} /></button>
      </div>
    </section>
  );
}
